/**
 * OrderPatientPicker.tsx
 *
 * Patient picker for the account-wide order buttons (Peptides, Partell): an
 * order form needs a patient context — shipping, payment, prescriber defaults —
 * so the patient is picked first, then handed to the order flow.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Syringe } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { searchPatients } from '@/services/patients.service';
import type { PatientSummary } from '@/types/patient.types';

export function OrderPatientPicker({
  isOpen,
  title,
  onClose,
  onSelect,
}: {
  isOpen: boolean;
  /** Says what is about to be ordered — once the picker is up nothing else on screen does. */
  title: string;
  onClose: () => void;
  onSelect: (patient: PatientSummary) => void;
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!isOpen) { setQuery(''); setDebounced(''); }
  }, [isOpen]);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['order-patient-search', debounced],
    queryFn: () => searchPatients(debounced),
    enabled: isOpen && debounced.length >= 2,
    staleTime: 30_000,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="md"
    >
      <div className="space-y-3">
        <p className="text-sm text-slate-600">Choose the patient this order is for.</p>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search patients by name, phone, or email…"
            className="pl-9"
          />
        </div>
        <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
          {debounced.length < 2 ? (
            <p className="p-4 text-center text-sm text-slate-400">Type at least 2 characters to search.</p>
          ) : isFetching ? (
            <p className="p-4 text-center text-sm text-slate-400">Searching…</p>
          ) : results.length === 0 ? (
            <p className="p-4 text-center text-sm text-slate-400">No patients match.</p>
          ) : (
            results.map((p) => (
              <button
                key={p.patientId}
                type="button"
                onClick={() => onSelect(p)}
                className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-blue-50"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-slate-800">{p.displayName}</span>
                  <span className="block truncate text-xs text-slate-500">
                    {[p.dateOfBirth ? `DOB ${p.dateOfBirth}` : null, p.cellPhone ?? p.phone, p.email]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                <Syringe className="h-4 w-4 shrink-0 text-blue-500" />
              </button>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
